import {Root as DialogRoot} from '@radix-ui/react-dialog';
import NextLink from 'next/link';

import {BirthSex, MeasurementUnit} from '#apollo/schema';
import {Button, Form, Link, Text} from '#component/base';
import {Modal} from '#component/base/Modal';
import {CombinedMeasurementInputField} from '#component/form/EditProfileForm/component';
import {FullscreenLoading, NumberInputField, TextInputField} from '#component/partial';
import {PRONOUN_OPTIONS} from '#constant/prounouns';
import {sprinkles} from '#style/sprinkles';

import {useFormAccountEdit} from './useFormAccountEdit';

import {FormAccountEditProps} from './FormAccountEdit.types';

import {personalInfoHeadingText} from './AccountTab.css';
import {
  formEditAccountLineWrapper,
  formEditAccountWrapper,
  inputEditAccountEmailTextMsg,
  inputEditAccountField,
} from './FormAccountEdit.css';

const FormAccountEdit = ({formData}: FormAccountEditProps) => {
  const {
    errors,
    isSubmitting,
    isHeightMetric,
    showBirthSexMessage,
    showResendMsg,
    isOpenEmail,
    loadingPass,
    signInLoading,
    currentEmail,
    register,
    handleSubmit,
    handleResendEmail,
    clearErrors,
    hookFormHandleSubmit,
    setIsOpenEmail,
    onChangeEmail,
    onChangeBirthSex,
    onChangePronouns,
    onChangePhoneNumber,
    onChangeHeightDisplayUnit,
    onChangeWeightDisplayUnit,
  } = useFormAccountEdit(formData);

  if (loadingPass || signInLoading) {
    return <FullscreenLoading />;
  }
  return (
    <Form
      className={formEditAccountWrapper}
      clearErrors={() =>
        clearErrors([
          'firstName',
          'lastName',
          'email',
          'currentPassword',
          'newPassword',
          'phoneNumber',
          'weight',
          'height',
          'pronouns',
        ])
      }
      errors={errors}
      onSubmit={hookFormHandleSubmit(handleSubmit)}>
      <div className={formEditAccountLineWrapper}>
        <TextInputField
          errors={errors}
          label="First Name"
          {...register('firstName')}
          className={inputEditAccountField}
        />
        <TextInputField
          errors={errors}
          label="Last Name"
          {...register('lastName')}
          className={inputEditAccountField}
        />
      </div>
      <div className={formEditAccountLineWrapper}>
        <NumberInputField
          errors={errors}
          label="Birthday"
          labelRightContent="Must be 18 or older"
          labelBottomContent="DOB ensures accurate results"
          mask="99/99/9999"
          maskPlaceholder="mm/dd/yyyy"
          {...register('birthday')}
          className={inputEditAccountField}
        />
        <TextInputField
          errors={errors}
          label="Birth Sex"
          className={inputEditAccountField}
          message={(htmlInputId: string) => {
            return showBirthSexMessage ? (
              <Text as="label" variant="caption" color="doveGray" htmlFor={htmlInputId}>
                Adding your birth sex helps us improve data accuracy
              </Text>
            ) : null;
          }}
          type="select"
          options={[
            {value: BirthSex.Unknown, label: 'Prefer not to say'},
            {value: BirthSex.Female, label: 'Female'},
            {value: BirthSex.Male, label: 'Male'},
          ]}
          {...register('birthSex', {
            onChange: onChangeBirthSex,
          })}
        />
      </div>
      <Text as="h1" variant="heading3" color="burgundy" className={personalInfoHeadingText}>
        Edit Password{' '}
      </Text>
      <div className={formEditAccountLineWrapper}>
        <TextInputField
          errors={errors}
          label="Current Password"
          type="password"
          {...register('currentPassword')}
          className={inputEditAccountField}
        />
        <TextInputField
          errors={errors}
          label="New Password"
          type="password"
          {...register('newPassword')}
          className={inputEditAccountField}
        />
      </div>
      <Text as="h1" variant="heading3" color="burgundy" className={personalInfoHeadingText}>
        Contact Information{' '}
      </Text>
      <div className={formEditAccountLineWrapper}>
        <TextInputField
          label="email"
          {...register('email', {
            onChange: onChangeEmail,
          })}
          className={inputEditAccountField}
          message={(htmlInputId: string) => {
            return showResendMsg ? (
              <div className={sprinkles({flex: 'flex', dir: 'col', gap: 12})}>
                <Text
                  as="label"
                  variant="caption"
                  color="doveGray"
                  htmlFor={htmlInputId}
                  className={inputEditAccountEmailTextMsg}>
                  Change verification link was sent to {currentEmail}
                </Text>
                <Text
                  as="button"
                  className={inputEditAccountEmailTextMsg}
                  variant="caption"
                  color="doveGray"
                  onClick={handleResendEmail}>
                  Resend
                </Text>
              </div>
            ) : null;
          }}
        />
        <NumberInputField
          errors={errors}
          label="Phone Number"
          mask="999-999-9999"
          maskPlaceholder="xxx-xxx-xxxx"
          placeholder="xxx-xxx-xxxx"
          type="phone"
          {...register('phoneNumber', {
            onChange: onChangePhoneNumber,
          })}
          className={inputEditAccountField}
        />
      </div>
      <Text as="h1" variant="heading3" color="burgundy" className={personalInfoHeadingText}>
        Additional Information{' '}
      </Text>
      <TextInputField
        errors={errors}
        label="Pronouns"
        type="select"
        options={PRONOUN_OPTIONS}
        {...register('pronouns', {
          onChange: onChangePronouns,
        })}
      />
      <div className={formEditAccountLineWrapper}>
        <CombinedMeasurementInputField
          label="Height"
          numberInputProps={{
            errors: errors,
            mask: isHeightMetric ? '' : '9\'99"',
            maskPlaceholder: 'x\'xx"',
            placeholder: isHeightMetric ? '' : 'x\'xx"',
            step: isHeightMetric ? '0.01' : 'undefined',
            ...register('height'),
          }}
          selectInputProps={{
            label: 'Height Measurement Unit',
            options: [
              {value: MeasurementUnit.Imperial, label: 'ft'},
              {value: MeasurementUnit.Metric, label: 'cm'},
            ],
            ...register('heightDisplayUnit'),
            onChange: onChangeHeightDisplayUnit,
          }}
        />
        <CombinedMeasurementInputField
          label="Weight"
          numberInputProps={{
            errors,
            mask: '',
            step: '0.01',
            type: 'number',
            ...register('weight'),
          }}
          selectInputProps={{
            label: 'Weight Measurement Unit',
            errors,
            options: [
              {value: MeasurementUnit.Imperial, label: 'lbs'},
              {value: MeasurementUnit.Metric, label: 'kg'},
            ],
            ...register('weightDisplayUnit'),
            onChange: onChangeWeightDisplayUnit,
          }}
        />
        <DialogRoot open={isOpenEmail} onOpenChange={setIsOpenEmail}>
          <Modal>
            <div className={sprinkles({flex: 'flex', dir: 'col', gap: 32})}>
              <Text variant="heading2" color="burgundy">
                Edit email
              </Text>

              <Text
                as="div"
                className={sprinkles({flex: 'flex', dir: 'col', gap: 64})}
                color="doveGray">
                <p>
                  {'We have sent a change verification email to '}
                  <Text color="inherit" fontWeight="semiBold">
                    {currentEmail}
                  </Text>
                  {'. Please click the link inside to activate your new email.'}
                </p>

                <p>
                  {'Didn’t receive your email? Check your spam or '}
                  <Link as="button" onClick={handleResendEmail}>
                    resend email.
                  </Link>
                </p>

                <Button disabled={isSubmitting} onClick={() => setIsOpenEmail(false)}>
                  Got it
                </Button>
              </Text>
            </div>
          </Modal>
        </DialogRoot>
      </div>
      <div className={formEditAccountLineWrapper}>
        <footer className={sprinkles({flex: 'flex', direction: 'col', gap: 32, width: 'full'})}>
          <div className={sprinkles({flex: 'flex', gap: [12, 12, 32], dir: ['col', 'col', 'row']})}>
            <Button disabled={isSubmitting} type="submit">
              Save
            </Button>
            <NextLink href="https://support.tallyhealth.com/en-US/contact" passHref>
              <Button as={'a'} type="button" variant="secondary" target="_blank">
                Contact Us
              </Button>
            </NextLink>
          </div>
        </footer>
      </div>
    </Form>
  );
};

export default FormAccountEdit;
